/**
 * Push Notification Service
 * Handles server-managed push notifications via FCM (Android) and APNs (iOS)
 * All notification scheduling is managed by the server
 */

import { PushNotifications, type Token, type ActionPerformed } from '@capacitor/push-notifications'
import { Capacitor } from '@capacitor/core'
import { logger } from '@/services/logger'
import type { PushNotificationPermissionStatus } from '@/types/notifications'

export interface DeviceTokenRegistration {
    token: string
    platform: 'ios' | 'android' | 'web'
    deviceId?: string
}

class PushNotificationServiceClass {
    private isInitialized = false
    private cachedToken?: DeviceTokenRegistration // Cache token in case callback is registered after token is received
    private registrationCallback?: (token: DeviceTokenRegistration) => void
    private notificationReceivedCallback?: (notification: any) => void
    private notificationActionCallback?: (action: ActionPerformed) => void

    /**
     * Check if push notifications are supported on this platform
     */
    isSupported(): boolean {
        return Capacitor.getPlatform() !== 'web'
    }

    /**
     * Initialize push notification service
     * Call this once during app startup
     */
    async initialize(): Promise<void> {
        if (!this.isSupported()) {
            logger.info('[PushNotificationService] Push notifications not supported on web platform')
            console.log('[PushNotificationService] Platform:', Capacitor.getPlatform())
            return
        }

        if (this.isInitialized) {
            logger.warn('[PushNotificationService] Already initialized')
            console.log('[PushNotificationService] Already initialized - skipping')
            return
        }

        try {
            console.log('[PushNotificationService] ========== STARTING INITIALIZATION ==========')

            // Check current permission status first
            console.log('[PushNotificationService] Checking current permission status...')
            const currentStatus = await this.checkPermissions()
            console.log('[PushNotificationService] Current permission status:', currentStatus)

            // Request permission
            console.log('[PushNotificationService] Requesting permissions...')
            const permissionStatus = await this.requestPermissions()
            console.log('[PushNotificationService] Permission request result:', permissionStatus)

            if (permissionStatus !== 'granted') {
                logger.warn('[PushNotificationService] Permission not granted:', { status: permissionStatus })
                console.error('[PushNotificationService] ❌ PERMISSION DENIED - Notifications will not work!')
                console.error('[PushNotificationService] Please enable notifications in device settings')
                return
            }

            console.log('[PushNotificationService] ✅ Permission granted, registering with FCM/APNs...')

            // Setup listeners BEFORE registering (to catch token immediately)
            this.setupListeners()

            // Register with FCM/APNs
            await PushNotifications.register()
            console.log('[PushNotificationService] ✅ Registration request sent to FCM/APNs')

            this.isInitialized = true
            logger.info('[PushNotificationService] Initialized successfully')
            console.log('[PushNotificationService] ========== INITIALIZATION COMPLETE ==========')
        } catch (error) {
            logger.error('[PushNotificationService] Failed to initialize:', error as Error)
            console.error('[PushNotificationService] ❌ INITIALIZATION FAILED:', error)
            throw error
        }
    }

    /**
     * Setup event listeners for push notifications
     */
    private setupListeners(): void {
        console.log('[PushNotificationService] Setting up event listeners...')

        // Token registration listener
        PushNotifications.addListener('registration', (token: Token) => {
            console.log('[PushNotificationService] ========== FCM TOKEN RECEIVED ==========')
            console.log('[PushNotificationService] Full token:', token.value)
            console.log('[PushNotificationService] Token length:', token.value.length)

            const deviceToken: DeviceTokenRegistration = {
                token: token.value,
                platform: Capacitor.getPlatform() as 'ios' | 'android'
            }

            logger.info('[PushNotificationService] Device registered:', {
                platform: deviceToken.platform,
                tokenPreview: token.value.substring(0, 20) + '...'
            })
            console.log('[PushNotificationService] Platform:', deviceToken.platform)

            // Cache the token in case callback is registered later
            this.cachedToken = deviceToken
            console.log('[PushNotificationService] Token cached for later use')

            // Call callback if it's already registered
            if (this.registrationCallback) {
                console.log('[PushNotificationService] ✅ Callback registered, calling it now...')
                this.registrationCallback(deviceToken)
            } else {
                logger.warn('[PushNotificationService] Token received but no callback registered yet - token cached for later')
                console.warn('[PushNotificationService] ⚠️ NO CALLBACK REGISTERED - Token will be sent when callback is registered')
            }
            console.log('[PushNotificationService] ========================================')
        })

        // Registration error listener
        PushNotifications.addListener('registrationError', (error: any) => {
            logger.error('[PushNotificationService] Registration error:', error)
            console.error('[PushNotificationService] ❌ FCM REGISTRATION ERROR:', JSON.stringify(error, null, 2))
        })

        // Push notification received (foreground)
        PushNotifications.addListener('pushNotificationReceived', (notification: any) => {
            console.log('[PushNotificationService] ========== NOTIFICATION RECEIVED (FOREGROUND) ==========')
            console.log('[PushNotificationService] Notification:', JSON.stringify(notification, null, 2))

            logger.info('[PushNotificationService] Notification received (foreground):', notification)

            if (this.notificationReceivedCallback) {
                console.log('[PushNotificationService] ✅ Calling notification received callback')
                this.notificationReceivedCallback(notification)
            } else {
                console.warn('[PushNotificationService] ⚠️ No callback registered for notification received')
            }
            console.log('[PushNotificationService] ====================================================')
        })

        // Push notification action performed (tap)
        PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
            console.log('[PushNotificationService] ========== NOTIFICATION TAPPED ==========')
            console.log('[PushNotificationService] Action:', JSON.stringify(action, null, 2))

            logger.info('[PushNotificationService] Notification action performed:', {
                actionId: action.actionId,
                notification: action.notification
            })

            if (this.notificationActionCallback) {
                console.log('[PushNotificationService] ✅ Calling notification action callback')
                this.notificationActionCallback(action)
            } else {
                console.warn('[PushNotificationService] ⚠️ No callback registered for notification action')
            }
            console.log('[PushNotificationService] =============================================')
        })

        console.log('[PushNotificationService] ✅ All event listeners registered')
    }

    /**
     * Request push notification permissions
     */
    async requestPermissions(): Promise<PushNotificationPermissionStatus> {
        if (!this.isSupported()) {
            return 'denied'
        }

        try {
            const result = await PushNotifications.requestPermissions()
            return result.receive as PushNotificationPermissionStatus
        } catch (error) {
            logger.error('[PushNotificationService] Failed to request permissions:', error as Error)
            return 'denied'
        }
    }

    /**
     * Check current permission status
     */
    async checkPermissions(): Promise<PushNotificationPermissionStatus> {
        if (!this.isSupported()) {
            return 'denied'
        }

        try {
            const result = await PushNotifications.checkPermissions()
            return result.receive as PushNotificationPermissionStatus
        } catch (error) {
            logger.error('[PushNotificationService] Failed to check permissions:', error as Error)
            return 'denied'
        }
    }

    /**
     * Register callback for when device token is received
     * The app should send this token to the server
     * If token was already received, callback is called immediately
     */
    onTokenReceived(callback: (token: DeviceTokenRegistration) => void): void {
        this.registrationCallback = callback

        // If token was already received before callback was registered, call it now
        if (this.cachedToken) {
            logger.info('[PushNotificationService] Callback registered after token received - calling with cached token')
            callback(this.cachedToken)
        }
    }

    /**
     * Register callback for when push notification is received in foreground
     */
    onNotificationReceived(callback: (notification: any) => void): void {
        this.notificationReceivedCallback = callback
    }

    /**
     * Register callback for when user taps on a notification
     */
    onNotificationAction(callback: (action: ActionPerformed) => void): void {
        this.notificationActionCallback = callback
    }

    /**
     * Get delivered notifications (iOS only)
     */
    async getDeliveredNotifications(): Promise<any[]> {
        if (!this.isSupported()) {
            return []
        }

        try {
            const result = await PushNotifications.getDeliveredNotifications()
            return result.notifications
        } catch (error) {
            logger.error('[PushNotificationService] Failed to get delivered notifications:', error as Error)
            return []
        }
    }

    /**
     * Remove delivered notifications (iOS only)
     */
    async removeDeliveredNotifications(ids: string[]): Promise<void> {
        if (!this.isSupported()) {
            return
        }

        try {
            await PushNotifications.removeDeliveredNotifications({
                notifications: ids.map(id => ({ id, title: '', body: '', data: {} }))
            })
            logger.info('[PushNotificationService] Removed delivered notifications:', { count: ids.length })
        } catch (error) {
            logger.error('[PushNotificationService] Failed to remove delivered notifications:', error as Error)
        }
    }

    /**
     * Remove all delivered notifications
     */
    async removeAllDeliveredNotifications(): Promise<void> {
        if (!this.isSupported()) {
            return
        }

        try {
            await PushNotifications.removeAllDeliveredNotifications()
            logger.info('[PushNotificationService] Removed all delivered notifications')
        } catch (error) {
            logger.error('[PushNotificationService] Failed to remove all delivered notifications:', error as Error)
        }
    }
}

// Export singleton instance
export const PushNotificationService = new PushNotificationServiceClass()

